import React, { useEffect, useState } from 'react';
import { Calendar, Plus, Trash2, Clock } from 'lucide-react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const DAYS = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo'];

export default function ScheduleManager() {
  const [schedules, setSchedules] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [startTime, setStartTime] = useState('08:00');
  const [endTime, setEndTime] = useState('12:00');
  const [selectedDays, setSelectedDays] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { fetchSchedules(); }, []);

  const fetchSchedules = async () => {
    try {
      const r = await api.get('/schedules');
      setSchedules(r.data.schedules || []);
    } catch (e) { console.error(e); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title || selectedDays.length === 0) { toast.error('Preencha todos os campos'); return; }
    setLoading(true);
    try {
      await api.post(`/schedules?title=${encodeURIComponent(title)}&start_time=${startTime}&end_time=${endTime}&days_of_week=${selectedDays.join(',')}`);
      toast.success('Cronograma adicionado!');
      setTitle(''); setSelectedDays([]); setShowForm(false);
      fetchSchedules();
    } catch (e) { toast.error('Erro ao adicionar'); }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/schedules/${id}`);
      toast.success('Removido!');
      fetchSchedules();
    } catch (e) { toast.error('Erro ao remover'); }
  };

  const toggleDay = (d) => setSelectedDays(p => p.includes(d) ? p.filter(x => x !== d) : [...p, d]);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 p-4 pb-24">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Meu Cronograma</h1>
          <Button onClick={() => setShowForm(!showForm)} className="gap-2">
            <Plus className="w-4 h-4" /> Adicionar
          </Button>
        </div>

        {showForm && (
          <Card className="border-2 border-blue-200 dark:border-blue-800">
            <CardHeader><CardTitle className="text-lg">Novo Compromisso</CardTitle></CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Atividade</Label>
                  <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Ex: Trabalho, Faculdade..." />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>Hora Início</Label><Input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} /></div>
                  <div><Label>Hora Fim</Label><Input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} /></div>
                </div>
                <div>
                  <Label>Dias da Semana</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {DAYS.map((d, i) => (
                      <Button key={i} type="button" size="sm" variant={selectedDays.includes(i) ? 'default' : 'outline'} onClick={() => toggleDay(i)}>
                        {d.slice(0, 3)}
                      </Button>
                    ))}
                  </div>
                </div>
                <Button type="submit" disabled={loading} className="w-full">{loading ? 'Salvando...' : 'Salvar'}</Button>
              </form>
            </CardContent>
          </Card>
        )}

        <Card className="border-2 border-blue-200 dark:border-blue-800">
          <CardHeader><CardTitle className="flex items-center gap-2"><Calendar className="w-5 h-5 text-blue-600" />Compromissos Fixos</CardTitle></CardHeader>
          <CardContent>
            {schedules.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-8">Nenhum compromisso cadastrado. Adicione seus horários fixos para calcularmos seu tempo livre!</p>
            ) : (
              <div className="space-y-3">
                {schedules.map(s => (
                  <div key={s.id} className="flex items-center justify-between p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                    <div>
                      <p className="font-medium text-slate-900 dark:text-slate-100">{s.title}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Clock className="w-3 h-3 text-slate-500" />
                        <span className="text-sm text-slate-600 dark:text-slate-400">{s.start_time} - {s.end_time}</span>
                        <div className="flex gap-1">
                          {(s.days_of_week || []).map(d => <Badge key={d} variant="secondary" className="text-xs">{DAYS[d]?.slice(0,3)}</Badge>)}
                        </div>
                      </div>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(s.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
